import {css, Theme} from "@emotion/react";
import {
    backgroundColor,
    calculateRemSize,
    flex,
    flexAlignCenter,
    flexDirection,
    mb,
    onDesktop,
    p,
    resetWidth,
    w100,
    zIndex
} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text";

import {CitySlug} from "../../../../config/cities";
import {RegionType} from "../../../../config/regions";
import {useAppSelector} from "../../../../utils/hooks/store_hooks";
import {AccessStatus, useAccessStatus} from "../../../../utils/hooks/useAccessStatus";
import {usePageTitle} from "../../../../utils/hooks/usePageTitle";
import {useChartsRegionId} from "../../features/analytical_platform/hooks/use_charts_region_id";
import {selectLatestEntry} from "../../features/ssr/redux/latest_entry_slice";
import {MobilePlatformNavTrigger} from "../nav/MobilePlatformNav";
import {CityMapAutoselect} from "./components/CityMapAutoselect";
// import {CitySelect} from "./components/CitySelect";
import {LimitedAccessBar, PreliminaryDataBar} from "./components/InfoBar";
import {PropertyTypeFilter} from "./components/PropertyTypeFilter";
// import {RegionTypeSwitch} from "./components/RegionTypeSwitch";

interface IPageHeaderProps {
    visible?: boolean;
    showCityAutoselect?: boolean;
    onCitySelection?: (city: CitySlug) => void;
    onRegionChange?: (region: RegionType) => void;
}

export const PageHeader = ({visible, showCityAutoselect = true}: IPageHeaderProps) => {
    const pageTitle = usePageTitle({});
    const accessStatus = useAccessStatus();
    const {completeData} = useAppSelector(selectLatestEntry);
    const {isRegionIdReady} = useChartsRegionId();
    const showPageTitle = !showCityAutoselect || isRegionIdReady;

    if (!visible) {
        return (
            <div css={mobileNavFallback}>
                <MobilePlatformNavTrigger />
            </div>
        );
    }

    return (
        <header css={pageHeader}>
            {showPageTitle ? (
                <Text css={[mb(3), onDesktop(mb(0)), title]} as="h1" variant="headline_4">
                    {pageTitle}
                </Text>
            ) : null}

            <div css={headerElements}>
                <div css={mobileFiltersGroup}>
                    <MobilePlatformNavTrigger />
                    <PropertyTypeFilter />
                </div>
                {/* {showRegionTypeSwitch && <RegionTypeSwitch onRegionTypeChange={onRegionChange} />} */}
                {/* <CitySelect onCitySelection={onCitySelection} /> */}
                {showCityAutoselect ? <CityMapAutoselect /> : null}
            </div>

            {/*(Mikołaj): Do not render bar if data is undefined*/}
            {accessStatus === AccessStatus.FULL && !completeData && <PreliminaryDataBar />}

            {accessStatus !== AccessStatus.FULL && <LimitedAccessBar />}
        </header>
    );
};

const mobileNavFallback = css`
    ${mb(2)};
    ${p(1.5, 1)};
    ${flex()};
    ${flexAlignCenter};

    ${onDesktop(css`
        display: none;
    `)};
`;

const pageHeader = (theme: Theme) => css`
    position: sticky;
    top: 0;
    ${zIndex(90)};
    ${mb(2)};
    ${p(1.5, 1)};
    ${flex("center", "space-between")}
    flex-wrap: wrap;
    align-items: center;
    gap: ${calculateRemSize(0.5)};
    ${backgroundColor(theme.colors.gray[100])};

    ${onDesktop(css`
        ${p(2, 3)};
    `)};
`;

const title = css`
    min-width: 0;

    ${onDesktop(css`
        flex: 1 1 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    `)}
`;

const headerElements = css`
    ${w100};
    ${flex()};
    ${flexDirection("column")}
    gap: ${calculateRemSize(3)};

    ${onDesktop(css`
        ${flexAlignCenter};
        ${flexDirection("row")};
        ${resetWidth};
        flex-shrink: 0;
        margin-left: auto;
    `)}
`;

const mobileFiltersGroup = css`
    ${w100};
    ${flex()};
    ${flexAlignCenter};
    gap: ${calculateRemSize(1.5)};

    ${onDesktop(css`
        display: contents;
    `)}
`;
